import _ from "lodash";
/** external */
import React from "react";
import { connect } from "react-redux";
import update from "immutability-helper";

/** internal */
import { compose, graphql } from "lib/apolloClient";
import withLoader from "UIComponents/GqlWithLoader";
import GenericTableEditor from "UIComponents/GenericTableEditor";
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";
import {
  getCurriculumProgramGradeBasicDetailsFromCache,
  getPlannerElementSetFromCache,
} from "PlannerElementSetConfiguration/modules/PlannerElementSetConfigurationGraphqlHelper";
import {
  getCurriculumProgramGradeBasicDetailsQuery,
  getPlannerElementSetQuery,
} from "PlannerElementSetConfiguration/modules/PlannerElementSetConfigurationQuery";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { getOrganizationMultiLevelNodeTagsQuery } from "modules/CommonQuery";
import { getOrganizationMultiLevelNodeTagsFromCache } from "modules/CommonGraphqlHelpers";
import {
  injectGradesInNodes,
  getPlannerElementNodeEditorConfig,
  formatPlannerElementNodes,
} from "PlannerElementSetConfiguration/modules/PlannerElementSetConfigurationUtils";
import { getPlannerElementSetPropertyValue } from "Fields/FieldUtils";

const NestedChecklistEditor = props => {
  const {
    plannerElementSet,
    gradeOptions,
    tagOptions,
    value,
    taggedNodesWithAncestors,
    updateValue,
  } = props;

  const { type, config } = plannerElementSet;

  const updatedPlannerElementSet = update(plannerElementSet, {
    nodes: nodes => injectGradesInNodes({ nodes }),
  });

  const { nodes, rootNode } = formatPlannerElementNodes(
    updatedPlannerElementSet
  );

  const currentEditorConfig = getPlannerElementNodeEditorConfig({
    setType: type + "_MULTI_WINDOW_TABLE",
    setConfig: config,
  });

  return (
    <GenericTableEditor
      showHeader={false}
      onSaveClick={_.noop}
      rootNode={rootNode}
      nodes={nodes}
      currentEditorConfig={currentEditorConfig}
      nodeType={type}
      maxDepth={5}
      gradeOptions={gradeOptions}
      tagOptions={tagOptions}
      value={value}
      taggedNodesWithAncestors={taggedNodesWithAncestors}
      updateValue={updateValue}
      mode="view"
    />
  );
};

const mapStateToProps = state => {
  const curriculumProgramId = _.get(
    state,
    "platform.currentCurriculumProgram.id",
    null
  );

  const userInfo = getUserInfo();

  return {
    isData: true,
    isLoading: false,
    areGradesRequired: false,
    areTagsRequired: false,
    organizationId: userInfo.org_id,
    curriculumProgramId,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getPlannerElementSetQuery, {
    name: "getPlannerElementSet",
    alias: "getPlannerElementSet",
    options: ({ plannerElementSetId }) => ({
      variables: {
        id: plannerElementSetId,
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getPlannerElementSet,
      ownProps: { isData, isLoading, plannerElementSetId, nodes },
    }) => {
      const data = getPlannerElementSetFromCache({
        plannerElementSetId,
      });

      const plannerElementSet = {
        config: _.get(data, "config", []),
        type: _.get(data, "type"),
        nodes,
      };

      const areGradesRequired = getPlannerElementSetPropertyValue({
        plannerElementSetConfig: plannerElementSet.config,
        propertyName: "allowGrades",
      });

      const areTagsRequired = getPlannerElementSetPropertyValue({
        plannerElementSetConfig: plannerElementSet.config,
        propertyName: "allowTags",
      });

      return {
        isData: isData && !_.isEmpty(data),
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getPlannerElementSet.networkStatus
          ) || isLoading,
        plannerElementSet,
        areGradesRequired,
        areTagsRequired,
      };
    },
  }),
  graphql(getCurriculumProgramGradeBasicDetailsQuery, {
    name: "getCurriculumProgramGradeBasicDetails",
    alias: "getCurriculumProgramGradeBasicDetails",
    skip: ({ areGradesRequired }) => !areGradesRequired,
    options: ({ organizationId, curriculumProgramId }) => ({
      variables: {
        organizationId,
        filters: {
          curriculumProgramIds: [curriculumProgramId],
        },
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getCurriculumProgramGradeBasicDetails,
      ownProps: { isData, isLoading, curriculumProgramId },
    }) => {
      const data = getCurriculumProgramGradeBasicDetailsFromCache(
        getCurriculumProgramGradeBasicDetails.variables
      );
      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getCurriculumProgramGradeBasicDetails.networkStatus
          ) || isLoading,
        gradeOptions: _.sortBy(
          _.filter(
            _.get(data, "grades", []),
            ({ curriculumProgram }) =>
              curriculumProgram.id === curriculumProgramId
          ),
          ["displaySequence"]
        ),
      };
    },
  }),
  graphql(getOrganizationMultiLevelNodeTagsQuery, {
    name: "getOrganizationMultiLevelNodeTags",
    alias: "getOrganizationMultiLevelNodeTags",
    skip: ({ areTagsRequired }) => !areTagsRequired,
    options: ({ organizationId }) => ({
      variables: {
        organizationId,
        filters: {
          types: ["GENERIC"],
        },
      },
      fetchPolicy: "cache-and-network",
    }),
    props: ({
      getOrganizationMultiLevelNodeTags,
      ownProps: { isData, isLoading },
    }) => {
      const data = getOrganizationMultiLevelNodeTagsFromCache(
        getOrganizationMultiLevelNodeTags.variables
      );
      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          _.includes(
            GRAPHQL_ONGOING_QUERY_NETWORK_STATUS,
            getOrganizationMultiLevelNodeTags.networkStatus
          ) || isLoading,
        tagOptions: _.get(data, "genericTagsV2", []),
      };
    },
  }),
  withLoader
)(NestedChecklistEditor);
